import { useEffect, useRef, useState } from "react";
import { Eraser, Download } from "lucide-react";

/**
 * Mouse / finger / stylus based signature pad.
 * Produces a dataURL (image/png) via onChange(dataUrl).
 */
export default function SignaturePad({ value, onChange, height = 180 }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  // Fit canvas pixel ratio for crisp output
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = "#0b0f17";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // If we have a previous value, draw it in
    if (value && value.startsWith("data:image")) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
    }
  }, [value]);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const begin = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    const c = canvasRef.current;
    try { onChange?.(c.toDataURL("image/png")); } catch { /* ignore */ }
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    onChange?.("");
  };

  const download = () => {
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url; a.download = "imza.png";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="sig-wrap">
      <canvas
        ref={canvasRef}
        style={{ height, width: "100%", touchAction: "none", background: "#ffffff", border: "1px dashed #d1d5db" }}
        onMouseDown={begin} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={begin} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <div className="sig-actions">
        <button type="button" className="chip" onClick={clear} data-testid="signature-clear"><Eraser size={12}/> Temizle</button>
        <button type="button" className="chip" onClick={download} data-testid="signature-download"><Download size={12}/> İndir</button>
      </div>
    </div>
  );
}
